import { Cart, ShippingMethod, ShippingRate } from "@commercetools/platform-sdk"
import { CentAmount } from "../../cart/CartCalculator"
import { useCommerceToolsClient } from "../../commercetools/CommerceToolsHooks"
import { ShippingMethodKeys } from "./ShippingMethods"

interface FetchShippingMethodsAttributes {
  cartId?: string
}

const filterShippingMethod = (m: ShippingMethod) =>
  m.key !== ShippingMethodKeys.expressShipping

const mapShippingMethod = (m: ShippingMethod, country = "") => {
  const countryToLanguage = c => (c === "us" || c === "is" ? c : "en")
  return {
    ...m,
    name: m.localizedDescription
      ? m.localizedDescription[countryToLanguage(country)] || m.name
      : m.name
  }
}

function arrangeShippingMethodsInOrder(it: ShippingMethod[]) {
  const [droppMethods, shippingMethodsWithoutDroppOptions] = it.reduce<
    ShippingMethod[][]
  >(
    ([left, right], method) =>
      method?.key?.includes("dropp")
        ? [left.concat(method), right]
        : [left, right.concat(method)],
    [[], []]
  )

  return [
    ...droppMethods.sort((left, right) =>
      (left.key || "") < (right.key || "") ? -1 : 1
    ),
    ...shippingMethodsWithoutDroppOptions
  ]
}

export const useShippingMethodsService = () => {
  const withClient = useCommerceToolsClient()

  const fetchShippingMethods = ({
    cartId
  }: FetchShippingMethodsAttributes): Promise<ShippingMethod[]> => {
    if (cartId) {
      return withClient
        .execute(it =>
          it.shippingMethods().matchingCart().get({ queryArgs: { cartId } })
        )
        .then(it => it.body.results.filter(filterShippingMethod))
        .then(arrangeShippingMethodsInOrder)
        .then(it => it.map(i => mapShippingMethod(i)))
    } else {
      return Promise.resolve([])
    }
  }

  const fetchShippingMethodsForLocation = ({
    country,
    currency
  }: {
    country: string
    currency: string
  }): Promise<ShippingMethod[]> => {
    return withClient
      .execute(it =>
        it
          .shippingMethods()
          .matchingLocation()
          .get({ queryArgs: { country: country.toUpperCase(), currency } })
      )
      .then(({ body: { results } }) => results.filter(filterShippingMethod))
      .then(arrangeShippingMethodsInOrder)
      .then(it => it.map(i => mapShippingMethod(i, country)))
  }

  const getShippingRate = (
    shippingMethod: ShippingMethod
  ): ShippingRate | undefined =>
    shippingMethod.zoneRates
      .flatMap(it => it.shippingRates)
      .find(it => it.isMatching)

  const isFreeShipping = (
    amount: CentAmount | undefined,
    rate: ShippingRate | undefined
  ): boolean => {
    if (amount?.centAmount && rate?.freeAbove?.centAmount) {
      return amount.centAmount > rate.freeAbove.centAmount
    }
    return false
  }

  const isIcelandPostbox = (shippingMethod: ShippingMethod) => {
    return shippingMethod.key === ShippingMethodKeys.icelandPostbox
  }

  const getShippingRateInfoForCart = (
    cart: Cart | undefined,
    shippingMethod: ShippingMethod
  ) => {
    const shippingRate = getShippingRate(shippingMethod)
    const price =
      (cart?.totalPrice?.centAmount || 0) -
      (cart?.shippingInfo?.price.centAmount || 0)
    return {
      shippingRate,
      isFreeShipping: isFreeShipping({ centAmount: price }, shippingRate),
      isIcelandPostbox: isIcelandPostbox(shippingMethod)
    }
  }

  return {
    fetchShippingMethods,
    fetchShippingMethodsForLocation,
    getShippingRate,
    getShippingRateInfoForCart,
    isFreeShipping,
    isIcelandPostbox
  }
}
